import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Phone, MapPin, FileText, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMERGENCY_CATEGORIES = [
  { value: 'FARRIER', label: 'Farrier', description: 'Hoof emergencies, lost shoes, lameness' },
  { value: 'VET', label: 'Veterinarian', description: 'Medical emergencies, injuries, illness' },
  { value: 'HORSE_HAULING', label: 'Horse Hauling', description: 'Emergency transport needed' },
  { value: 'FENCE_REPAIR', label: 'Fence Repair', description: 'Broken fencing, escaped animals' },
  { value: 'RANCH_REPAIR', label: 'Ranch Repair', description: 'Barn, stall, or facility damage' },
  { value: 'EQUIPMENT_REPAIR', label: 'Equipment Repair', description: 'Tractor, trailer, or machinery' },
];

export default function EmergencyPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    location: '',
    category: '',
    description: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.clientName || !formData.clientPhone || !formData.location || !formData.category || !formData.description) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await fetch(`${API_URL}/api/emergency-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) throw new Error('Failed to submit request');
      
      setSubmitted(true);
      toast.success('Emergency request submitted!');
    } catch (error) {
      console.error('Error submitting emergency request:', error);
      toast.error('Failed to submit request. Please try again or call directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-card border-b">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <Logo variant="compact" size="sm" />
          </div>
        </nav>
        
        <div className="max-w-lg mx-auto px-4 py-12">
          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-2xl">Request Submitted!</CardTitle>
              <CardDescription className="text-base mt-2">
                Your emergency request has been sent to local service providers.
                Someone will contact you shortly at {formData.clientPhone}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg text-sm space-y-1">
                <p><strong>Service:</strong> {EMERGENCY_CATEGORIES.find(c => c.value === formData.category)?.label}</p>
                <p><strong>Location:</strong> {formData.location}</p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/')} variant="outline" className="flex-1">
                  Back to Home
                </Button>
                <Button onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    clientName: '',
                    clientPhone: '',
                    location: '',
                    category: '',
                    description: '',
                  });
                }} className="flex-1">
                  Submit Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="bg-card border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Logo variant="compact" size="sm" />
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Emergency Banner */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-red-800">Emergency Service Request</h2>
            <p className="text-sm text-red-700 mt-1">
              For life-threatening emergencies, please call 911 or your local emergency vet directly.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Request Emergency Service
            </CardTitle>
            <CardDescription>
              Submit your request and available providers will be notified immediately.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="clientName">Your Name *</Label>
                <Input
                  id="clientName"
                  data-testid="emergency-name-input"
                  placeholder="Full name"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="clientPhone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number *
                </Label>
                <Input
                  id="clientPhone"
                  data-testid="emergency-phone-input"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.clientPhone}
                  onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Location *
                </Label>
                <Input
                  id="location"
                  data-testid="emergency-location-input"
                  placeholder="Address or location description"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Service Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger id="category" data-testid="emergency-category-select">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMERGENCY_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{cat.label}</span>
                          <span className="text-xs text-muted-foreground">{cat.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Description of Emergency *
                </Label>
                <Textarea
                  id="description"
                  data-testid="emergency-description-input"
                  placeholder="Describe the emergency situation and what service you need..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
                disabled={submitting}
                data-testid="emergency-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="mr-2 h-5 w-5" />
                    Submit Emergency Request
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>
            After submitting, providers in your area matching your service need will be notified.
            They will contact you directly to arrange service.
          </p>
        </div>
      </div>
    </div>
  );
}
